import React, { useState, useEffect } from 'react';
import { Download, Clock, Star, Moon, Sun, Building2, Calendar, MapPin, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
               'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

// Excellent nakshatras for business
const EXCELLENT_NAKSHATRAS = ['Rohini', 'Pushya', 'Uttara Phalguni', 'Hasta', 'Anuradha', 
                              'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Uttara Bhadrapada', 'Revati'];
const GOOD_NAKSHATRAS = ['Ashwini', 'Mrigashira', 'Punarvasu', 'Magha', 'Chitra', 'Swati', 'Vishakha'];
const BUSINESS_LAGNAS = ['Taurus', 'Leo', 'Virgo', 'Libra', 'Capricorn', 'Aquarius'];

export default function IncorporationKernelPage() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [kernelData, setKernelData] = useState(null);
  const [loadingKernel, setLoadingKernel] = useState(false);
  const [expandedSlot, setExpandedSlot] = useState(null);
  
  // Configuration
  const [config, setConfig] = useState({
    year: 2026,
    month: 2,
    day: 25,
    companyName: 'Taraanaya B2C',
    startHour: 8,
    endHour: 22,
    interval: 15
  });

  useEffect(() => {
    fetchSlots();
  }, [config]);

  const fetchSlots = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_URL}/api/mandeep-kernel/incorporation-slots/${config.year}/${config.month}/${config.day}?company_name=${encodeURIComponent(config.companyName)}&start_hour=${config.startHour}&end_hour=${config.endHour}&interval_minutes=${config.interval}`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      setSlots(data.slots || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchKernel = async (slot) => {
    setLoadingKernel(true);
    setSelectedSlot(slot);
    try {
      const url = `${API_URL}/api/mandeep-kernel/incorporation-kernel/${config.year}/${config.month}/${config.day}/${slot.hour}/${slot.minute}?company_name=${encodeURIComponent(config.companyName)}`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      setKernelData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingKernel(false);
    }
  };

  const downloadKernel = async (slot) => {
    try {
      const url = `${API_URL}/api/mandeep-kernel/incorporation-kernel/${config.year}/${config.month}/${config.day}/${slot.hour}/${slot.minute}?company_name=${encodeURIComponent(config.companyName)}`;
      const response = await fetch(url);
      const data = await response.json();
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${config.companyName.replace(/\s+/g, '_')}_${slot.time_24h.replace(':', '')}_kernel.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      alert('Download failed: ' + err.message);
    }
  };

  const getRating = (slot) => {
    let score = 0;
    
    // Business lagna
    if (BUSINESS_LAGNAS.includes(slot.lagna)) score += 30;
    
    // Excellent nakshatra
    if (EXCELLENT_NAKSHATRAS.includes(slot.lagna_nakshatra)) score += 25;
    else if (GOOD_NAKSHATRAS.includes(slot.lagna_nakshatra)) score += 15;
    
    // Moon exalted
    if (slot.is_moon_exalted) score += 30;
    
    // Moon nakshatra
    if (EXCELLENT_NAKSHATRAS.includes(slot.moon_nakshatra)) score += 15;
    
    if (score >= 70) return { stars: 3, label: 'EXCELLENT', color: 'text-yellow-400' };
    if (score >= 50) return { stars: 2, label: 'GOOD', color: 'text-green-400' };
    if (score >= 30) return { stars: 1, label: 'OK', color: 'text-blue-400' };
    return { stars: 0, label: 'AVOID', color: 'text-gray-500' };
  };

  const renderStars = (count) => {
    return Array(3).fill(0).map((_, i) => (
      <Star 
        key={i} 
        size={14} 
        className={i < count ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'} 
      />
    ));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-900/30 via-gray-900 to-amber-900/30 border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-amber-400 flex items-center gap-3">
                <Building2 className="text-amber-500" />
                Company Incorporation Muhurta
              </h1>
              <p className="text-gray-400 mt-1">
                Complete Parashara Kernel for Every Time Slot
              </p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 text-amber-400">
                <Calendar size={18} />
                <span className="text-lg font-semibold">{config.day}/{config.month}/{config.year}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400 mt-1">
                <MapPin size={14} />
                <span className="text-sm">Delhi, India</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Panel */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Company Name</label>
              <input
                type="text"
                value={config.companyName}
                onChange={(e) => setConfig({...config, companyName: e.target.value})}
                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                data-testid="company-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Date</label>
              <input
                type="date"
                value={`${config.year}-${String(config.month).padStart(2,'0')}-${String(config.day).padStart(2,'0')}`}
                onChange={(e) => {
                  const [y, m, d] = e.target.value.split('-');
                  setConfig({...config, year: parseInt(y), month: parseInt(m), day: parseInt(d)});
                }}
                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                data-testid="date-input"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Start Hour</label>
              <select
                value={config.startHour}
                onChange={(e) => setConfig({...config, startHour: parseInt(e.target.value)})}
                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white"
              >
                {Array.from({length: 24}, (_, i) => (
                  <option key={i} value={i}>{i}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">End Hour</label>
              <select
                value={config.endHour}
                onChange={(e) => setConfig({...config, endHour: parseInt(e.target.value)})}
                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white"
              >
                {Array.from({length: 24}, (_, i) => (
                  <option key={i} value={i}>{i}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Interval</label>
              <select
                value={config.interval}
                onChange={(e) => setConfig({...config, interval: parseInt(e.target.value)})}
                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white"
              >
                <option value={15}>15 mins</option>
                <option value={30}>30 mins</option>
                <option value={60}>1 hour</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-amber-400" size={40} />
            <span className="ml-3 text-lg text-gray-400">Loading time slots...</span>
          </div>
        ) : error ? (
          <div className="bg-red-900/30 border border-red-500 rounded-xl p-6 text-center">
            <p className="text-red-400">{error}</p>
            <button 
              onClick={fetchSlots}
              className="mt-4 px-4 py-2 bg-red-600 rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="grid gap-3">
            {/* Header Row */}
            <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-800/50 rounded-lg text-xs text-gray-400 font-semibold">
              <div className="col-span-1">Time</div>
              <div className="col-span-2">Lagna</div>
              <div className="col-span-2">Lagna Nakshatra</div>
              <div className="col-span-2">Moon Sign</div>
              <div className="col-span-2">Moon Nakshatra</div>
              <div className="col-span-1">Rating</div>
              <div className="col-span-2 text-center">Actions</div>
            </div>

            {/* Time Slots */}
            {slots.map((slot, idx) => {
              const rating = getRating(slot);
              const isExpanded = expandedSlot === idx;
              
              return (
                <div key={idx} className="bg-gray-800/30 rounded-xl border border-gray-700/50 hover:border-amber-500/30 transition-all">
                  {/* Main Row */}
                  <div 
                    className="grid grid-cols-12 gap-2 px-4 py-3 items-center cursor-pointer"
                    onClick={() => setExpandedSlot(isExpanded ? null : idx)}
                    data-testid={`slot-row-${slot.time_24h.replace(':', '')}`}
                  >
                    <div className="col-span-1">
                      <div className="flex items-center gap-1">
                        <Clock size={14} className="text-amber-500" />
                        <span className="font-mono font-semibold text-white">{slot.time_12h}</span>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <span className={`font-semibold ${BUSINESS_LAGNAS.includes(slot.lagna) ? 'text-green-400' : 'text-gray-300'}`}>
                        {slot.lagna}
                      </span>
                      <span className="text-gray-500 text-xs ml-1">{slot.lagna_degree}°</span>
                    </div>
                    <div className="col-span-2">
                      <span className={`text-sm ${EXCELLENT_NAKSHATRAS.includes(slot.lagna_nakshatra) ? 'text-green-400' : GOOD_NAKSHATRAS.includes(slot.lagna_nakshatra) ? 'text-blue-400' : 'text-gray-400'}`}>
                        {slot.lagna_nakshatra}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-1">
                        <Moon size={12} className={slot.is_moon_exalted ? 'text-yellow-400' : 'text-gray-500'} />
                        <span className={slot.is_moon_exalted ? 'text-yellow-400 font-semibold' : 'text-gray-300'}>
                          {slot.moon_sign}
                          {slot.is_moon_exalted && <span className="text-xs ml-1">(Exalted)</span>}
                        </span>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <span className={`text-sm ${EXCELLENT_NAKSHATRAS.includes(slot.moon_nakshatra) ? 'text-green-400' : 'text-gray-400'}`}>
                        {slot.moon_nakshatra}
                      </span>
                    </div>
                    <div className="col-span-1">
                      <div className="flex items-center gap-1">
                        {renderStars(rating.stars)}
                      </div>
                      <span className={`text-xs ${rating.color}`}>{rating.label}</span>
                    </div>
                    <div className="col-span-2 flex justify-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadKernel(slot);
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 rounded text-xs font-semibold transition-colors"
                        data-testid={`download-btn-${slot.time_24h.replace(':', '')}`}
                      >
                        <Download size={12} />
                        Download
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchKernel(slot);
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-xs font-semibold transition-colors"
                        data-testid={`view-btn-${slot.time_24h.replace(':', '')}`}
                      >
                        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        View
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && selectedSlot?.time_24h === slot.time_24h && kernelData && (
                    <div className="px-4 pb-4 border-t border-gray-700/50 mt-2 pt-4">
                      {loadingKernel ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="animate-spin text-amber-400" size={24} />
                          <span className="ml-2 text-gray-400">Loading complete kernel...</span>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Birth Details */}
                          <div className="bg-gray-900/50 rounded-lg p-4">
                            <h4 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                              <Building2 size={16} />
                              Incorporation Details
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-400">Company:</span>
                                <span className="text-white">{kernelData.name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Date:</span>
                                <span className="text-white">{kernelData.birth_particulars?.date}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Time:</span>
                                <span className="text-white">{kernelData.birth_particulars?.time}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Ayanamsa:</span>
                                <span className="text-white">{kernelData.astronomical_data?.ayanamsa}</span>
                              </div>
                            </div>
                          </div>

                          {/* Lagna & Moon */}
                          <div className="bg-gray-900/50 rounded-lg p-4">
                            <h4 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                              <Sun size={16} />
                              Lagna & Moon
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-400">Lagna:</span>
                                <span className="text-white">{kernelData.astronomical_data?.lagna}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Lagna Lord:</span>
                                <span className="text-white">{kernelData.astronomical_data?.lagna_lord}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Moon Sign:</span>
                                <span className="text-white">{kernelData.planets?.Moon?.sign}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Moon Nakshatra:</span>
                                <span className="text-white">{kernelData.planets?.Moon?.nakshatra}</span>
                              </div>
                            </div>
                          </div>

                          {/* Panchang */}
                          <div className="bg-gray-900/50 rounded-lg p-4">
                            <h4 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                              <Calendar size={16} />
                              Panchang
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-400">Tithi:</span>
                                <span className="text-white">{kernelData.panchang?.tithi?.full_name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Nakshatra:</span>
                                <span className="text-white">{kernelData.panchang?.nakshatra?.name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Yoga:</span>
                                <span className="text-white">{kernelData.panchang?.yoga?.name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Karana:</span>
                                <span className="text-white">{kernelData.panchang?.karana?.name}</span>
                              </div>
                            </div>
                          </div>

                          {/* Planets Table */}
                          <div className="md:col-span-3 bg-gray-900/50 rounded-lg p-4">
                            <h4 className="text-amber-400 font-semibold mb-3">Planet Positions (D1)</h4>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-gray-400 border-b border-gray-700">
                                    <th className="text-left py-2">Planet</th>
                                    <th className="text-left py-2">Sign</th>
                                    <th className="text-left py-2">Degree</th>
                                    <th className="text-left py-2">Nakshatra</th>
                                    <th className="text-left py-2">House</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'].map(planet => {
                                    const p = kernelData.planets?.[planet];
                                    if (!p) return null;
                                    return (
                                      <tr key={planet} className="border-b border-gray-800">
                                        <td className="py-2 text-white font-medium">{planet}</td>
                                        <td className="py-2 text-gray-300">{p.sign}</td>
                                        <td className="py-2 text-gray-300">{p.degree?.toFixed(2)}°</td>
                                        <td className="py-2 text-gray-300">{p.nakshatra}</td>
                                        <td className="py-2 text-gray-300">H{p.house}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Summary Footer */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-gradient-to-r from-amber-900/20 via-gray-800/50 to-amber-900/20 rounded-xl p-6 border border-amber-500/20">
          <h3 className="text-xl font-bold text-amber-400 mb-4">Muhurta Rating Guide</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-3">
              <div className="flex">{renderStars(3)}</div>
              <span className="text-yellow-400">EXCELLENT - Highly Recommended</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex">{renderStars(2)}</div>
              <span className="text-green-400">GOOD - Recommended</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex">{renderStars(1)}</div>
              <span className="text-blue-400">OK - Acceptable</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex">{renderStars(0)}</div>
              <span className="text-gray-500">AVOID - Not Recommended</span>
            </div>
          </div>
          <div className="mt-4 text-xs text-gray-400">
            <p><strong>Best Lagnas:</strong> Taurus, Leo, Virgo, Libra, Capricorn, Aquarius</p>
            <p><strong>Best Nakshatras:</strong> Rohini, Pushya, Hasta, Anuradha, Shravana, Revati</p>
            <p><strong>Moon Exalted:</strong> When Moon is in Taurus sign</p>
          </div>
        </div>
      </div>
    </div>
  );
}
